import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MemberShell from "@/components/MemberShell";
import { MEMBER_DASH_NAV_BASE } from "@/lib/memberDashboardNav";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import StripePaymentDialog from "@/components/StripePaymentDialog";
import { humanizeAuthError } from "@/lib/authErrors";
import { formatPhoneForDisplay, formatPhoneInputLive, normalizeInternationalPhone } from "@/lib/phoneFormat";
import { CreditCard, FileText, KeyRound, User } from "lucide-react";

/** Dérive prénom/nom à partir du profil ; si seul `name` existe, on le coupe grossièrement en deux. */
function deriveAccountFormFromUser(u) {
  let first = String(u?.first_name ?? "").trim();
  let last = String(u?.last_name ?? "").trim();
  const rawName = String(u?.name ?? u?.nom ?? "").trim();

  if (!first && !last && rawName) {
    const parts = rawName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      first = parts[0];
    } else if (parts.length > 1) {
      first = parts[0];
      last = parts.slice(1).join(" ");
    }
  }

  const telRaw = String(u?.telephone ?? u?.phone ?? "").trim();
  const tel = telRaw ? formatPhoneForDisplay(telRaw) : "";
  const country = String(u?.country ?? "").trim();

  let company = String(u?.company_name ?? "").trim();
  const full = [first, last].filter(Boolean).join(" ").trim();
  const fullLower = full.toLowerCase();
  if (company && (company.toLowerCase() === fullLower || company.toLowerCase() === first.toLowerCase())) {
    company = "";
  }

  return {
    first_name: first,
    last_name: last,
    telephone: tel,
    company_name: company,
    country,
    siret: String(u?.siret ?? "").trim(),
    company_legal_form: String(u?.company_legal_form ?? "").trim(),
    company_vat_number: String(u?.company_vat_number ?? "").trim(),
    company_address_line1: String(u?.company_address_line1 ?? "").trim(),
    company_postal_code: String(u?.company_postal_code ?? "").trim(),
    company_city: String(u?.company_city ?? "").trim(),
  };
}

const Account = () => {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  /** false = n’afficher que les champs déjà enregistrés en base ; true = formulaire complet. */
  const [coordExpanded, setCoordExpanded] = useState(false);

  const [emailNew, setEmailNew] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [pwdBusy, setPwdBusy] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editPasswordOpen, setEditPasswordOpen] = useState(false);

  const [paymentMethodsState, setPaymentMethodsState] = useState({
    loading: false,
    items: [],
    default_id: null,
    stripe_customer_id: null,
  });
  const [pmSetup, setPmSetup] = useState({ open: false, clientSecret: "", busy: false });
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [detachingId, setDetachingId] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    telephone: "",
    company_name: "",
    country: "",
    siret: "",
    company_legal_form: "",
    company_vat_number: "",
    company_address_line1: "",
    company_postal_code: "",
    company_city: "",
  });
  const [billingSaving, setBillingSaving] = useState(false);

  useEffect(() => {
    setForm(deriveAccountFormFromUser(user));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const loadSub = async () => {
      if (!user?.user_id) {
        setSubLoading(false);
        return;
      }
      setSubLoading(true);
      try {
        const res = await api.get("/subscriptions/me");
        if (cancelled) return;
        setSubscription(res?.data?.subscription ?? null);
      } catch {
        if (cancelled) return;
        setSubscription(null);
      } finally {
        if (!cancelled) setSubLoading(false);
      }
    };
    void loadSub();
    return () => {
      cancelled = true;
    };
  }, [user?.user_id]);

  const loadPaymentMethods = useCallback(async () => {
    if (!user?.user_id) return;
    setPaymentMethodsState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get("/payment-methods/list");
      const d = res?.data || {};
      setPaymentMethodsState({
        loading: false,
        items: Array.isArray(d.payment_methods) ? d.payment_methods : [],
        default_id: d.default_payment_method_id || null,
        stripe_customer_id: d.stripe_customer_id || null,
      });
    } catch {
      setPaymentMethodsState((s) => ({ ...s, loading: false, items: [] }));
    }
  }, [user?.user_id]);

  useEffect(() => {
    void loadPaymentMethods();
  }, [loadPaymentMethods]);

  const startAddPaymentMethod = async () => {
    setPmSetup((p) => ({ ...p, busy: true }));
    try {
      const res = await api.post("/payment-methods/setup-intent", {});
      const cs = res?.data?.clientSecret || res?.data?.client_secret || "";
      if (!cs) throw new Error("Setup indisponible");
      setPmSetup({ open: true, clientSecret: String(cs), busy: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Impossible d’ajouter une carte");
      setPmSetup({ open: false, clientSecret: "", busy: false });
    }
  };

  const setCardAsDefault = async (payment_method_id) => {
    setSettingDefaultId(payment_method_id);
    try {
      await api.post("/payment-methods/set-default", { payment_method_id });
      toast.success("Carte définie par défaut.");
      await loadPaymentMethods();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de mettre à jour");
    } finally {
      setSettingDefaultId(null);
    }
  };

  const removePaymentMethod = async (payment_method_id) => {
    setDetachingId(payment_method_id);
    try {
      await api.post("/payment-methods/detach", { payment_method_id });
      toast.success("Carte retirée.");
      await loadPaymentMethods();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de retirer cette carte");
    } finally {
      setDetachingId(null);
    }
  };

  const subscriptionBlock = useMemo(() => {
    if (subLoading) return { badge: "…", detail: "Vérification en cours", tone: "bg-slate-100 text-slate-700" };
    if (!subscription)
      return {
        badge: "Aucun abonnement",
        detail: "Requis pour acheter sur la marketplace.",
        tone: "bg-amber-50 text-amber-900 ring-1 ring-amber-200",
      };
    const end = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
    const active = subscription.statut === "active" && (!end || end.getTime() > Date.now());
    if (!active)
      return {
        badge: "Inactif ou expiré",
        detail: "Réactivez un abonnement pour continuer à acheter.",
        tone: "bg-slate-100 text-slate-800",
      };
    return {
      badge: "Abonnement actif",
      detail: "",
      tone: "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200",
    };
  }, [subscription, subLoading]);

  const displayInitial = useMemo(() => {
    const f = String(form.first_name || "").trim();
    const l = String(form.last_name || "").trim();
    const letter = (f[0] || l[0] || user?.email?.[0] || "?").toUpperCase();
    return letter;
  }, [form.first_name, form.last_name, user?.email]);

  /** Champs profil réellement renseignés (hors pseudo « nom » dérivé du préfixe e-mail). */
  const coordCollected = useMemo(() => {
    const u = user;
    if (!u) {
      return {
        hasIdentity: false,
        hasPhone: false,
        hasCompany: false,
        hasCountry: false,
        hasAny: false,
      };
    }
    const emailLocal = String(u.email || "")
      .split("@")[0]
      ?.toLowerCase()
      .trim();
    const displayName = String(u.name ?? u.nom ?? "").trim();
    const isAutoName = Boolean(emailLocal && displayName.toLowerCase() === emailLocal);
    const first = String(u.first_name ?? "").trim();
    const last = String(u.last_name ?? "").trim();
    const hasRealName = Boolean(first || last);
    const hasCollectedFullName = Boolean(displayName && !isAutoName);
    const hasIdentity = hasRealName || hasCollectedFullName;
    const phone = String(u.telephone ?? u.phone ?? "").trim();
    const hasPhone = Boolean(phone);
    const company = String(u.company_name ?? "").trim();
    const country = String(u.country ?? "").trim();
    const hasCompany = Boolean(company);
    const hasCountry = Boolean(country);
    const hasAny = hasIdentity || hasPhone || hasCompany || hasCountry;
    return { hasIdentity, hasPhone, hasCompany, hasCountry, hasAny };
  }, [user]);

  const save = async () => {
    if (!user?.user_id) return;
    const first = String(form.first_name || "").trim();
    const last = String(form.last_name || "").trim();
    const fullName = [first, last].filter(Boolean).join(" ").trim();
    let telephoneOut = String(form.telephone || "").trim() || null;
    if (telephoneOut) {
      try {
        telephoneOut = normalizeInternationalPhone(telephoneOut);
      } catch (e) {
        return toast.error(e?.message || "Téléphone invalide");
      }
    }
    const payload = {
      name: fullName || null,
      nom: fullName || null,
      first_name: first || null,
      last_name: last || null,
      telephone: telephoneOut,
      phone: telephoneOut,
    };
    setSaving(true);
    try {
      const { error } = await supabase.from("profiles").update(payload).eq("id", user.user_id);
      if (error) throw error;
      toast.success("Profil enregistré");
      setCoordExpanded(false);
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Mise à jour impossible");
    } finally {
      setSaving(false);
    }
  };

  const submitEmailChange = async () => {
    if (!user?.user_id) return;
    const next = String(emailNew).trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      toast.error("Indiquez une adresse e-mail valide.");
      return;
    }
    const current = String(user?.email || "").trim().toLowerCase();
    if (next === current) {
      toast.info("C’est déjà votre adresse.");
      return;
    }
    setEmailBusy(true);
    try {
      const invalidCurrent =
        current && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(current);
      if (invalidCurrent) {
        const { data } = await api.post("/account/change-email", { email: next });
        toast.success(
          data?.previous_invalid
            ? `E-mail mis à jour : ${data.email || next} (l’ancienne adresse ${current} n’était pas valide pour Supabase).`
            : `E-mail mis à jour : ${data?.email || next}.`,
        );
      } else {
        const { error } = await supabase.auth.updateUser({ email: next });
        if (error) {
          const msg = String(error.message || "");
          if (/invalid/i.test(msg) && current) {
            const { data } = await api.post("/account/change-email", { email: next });
            toast.success(`E-mail mis à jour : ${data?.email || next}.`);
          } else {
            throw error;
          }
        } else {
          try {
            await supabase.from("profiles").update({ email: next }).eq("id", user.user_id);
          } catch {
            /* ignore */
          }
          toast.success(
            "Demande enregistrée. Si votre projet l’exige, ouvrez le lien de confirmation envoyé sur la nouvelle adresse.",
          );
        }
      }
      setEmailNew("");
      setEditEmailOpen(false);
      await refresh();
    } catch (e) {
      toast.error(humanizeAuthError(e?.response?.data?.detail || e?.message));
    } finally {
      setEmailBusy(false);
    }
  };

  const submitPasswordChange = async () => {
    if (pwdNew.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (pwdNew !== pwdConfirm) {
      toast.error("Les deux saisies du nouveau mot de passe ne correspondent pas.");
      return;
    }
    setPwdBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwdNew });
      if (error) throw error;
      toast.success("Mot de passe mis à jour.");
      setPwdNew("");
      setPwdConfirm("");
      setEditPasswordOpen(false);
    } catch (e) {
      toast.error(humanizeAuthError(e?.message));
    } finally {
      setPwdBusy(false);
    }
  };

  const saveBilling = async () => {
    if (!user?.user_id) return;
    const payload = {
      company_name: String(form.company_name || "").trim() || null,
      company_legal_form: String(form.company_legal_form || "").trim() || null,
      siret: String(form.siret || "").replace(/\s/g, "") || null,
      company_vat_number: String(form.company_vat_number || "").trim() || null,
      company_address_line1: String(form.company_address_line1 || "").trim() || null,
      company_postal_code: String(form.company_postal_code || "").trim() || null,
      company_city: String(form.company_city || "").trim() || null,
      country: String(form.country || "").trim().toUpperCase() || null,
    };
    setBillingSaving(true);
    try {
      const { error } = await supabase.from("profiles").update(payload).eq("id", user.user_id);
      if (error) throw error;
      toast.success("Informations de facturation enregistrées");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Mise à jour impossible");
    } finally {
      setBillingSaving(false);
    }
  };

  const billingIncomplete = useMemo(() => {
    const need = [
      form.company_name,
      form.company_address_line1,
      form.company_postal_code,
      form.company_city,
      form.country,
    ];
    return need.some((v) => !String(v || "").trim());
  }, [form]);

  const showCoordIdentity = coordExpanded || coordCollected.hasIdentity;
  const showCoordPhone = coordExpanded || coordCollected.hasPhone;

  const hasCards = paymentMethodsState.items.length > 0;
  const paymentEmptyMessage =
    !paymentMethodsState.stripe_customer_id && !hasCards
      ? "Ajoutez une carte pour payer plus vite sur la marketplace (après abonnement ou via le bouton ci-dessous)."
      : !hasCards
        ? "Aucune carte pour l’instant — utilisez « Ajouter une carte »."
        : null;

  return (
    <>
      <MemberShell
        title="Mon compte"
        subtitle="E-mail, mot de passe, coordonnées et carte pour vos achats."
        dashboardNav={MEMBER_DASH_NAV_BASE}
      >
        <div className="mx-auto max-w-2xl">

        {/* Résumé */}
        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#2D7FF9] font-display text-2xl font-black text-white">
            {displayInitial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-display text-lg font-bold text-[#0B0D12]">{user?.email || "—"}</span>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${subscriptionBlock.tone}`}>
                {subscriptionBlock.badge}
              </span>
            </div>
            {subscriptionBlock.detail ? (
              <p className="mt-1 text-sm text-slate-600">{subscriptionBlock.detail}</p>
            ) : null}
          </div>
        </div>

        {/* Connexion */}
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-slate-900">
            <KeyRound className="h-5 w-5 text-[#2D7FF9]" />
            <h2 className="font-display text-lg font-bold">Connexion</h2>
          </div>

          <div className="space-y-6">
            <div>
              <Label htmlFor="acc-email-current" className="text-slate-800">
                Adresse e-mail
              </Label>
              <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p id="acc-email-current" className="text-sm font-medium text-[#0B0D12]">
                  {user?.email || "—"}
                </p>
                {!editEmailOpen ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 rounded-xl"
                    onClick={() => {
                      setEditPasswordOpen(false);
                      setPwdNew("");
                      setPwdConfirm("");
                      setEditEmailOpen(true);
                    }}
                  >
                    Modifier l’adresse
                  </Button>
                ) : null}
              </div>
              {editEmailOpen ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Label htmlFor="acc-email-new" className="text-xs text-slate-600">
                        Nouvelle adresse
                      </Label>
                      <Input
                        id="acc-email-new"
                        type="email"
                        value={emailNew}
                        onChange={(e) => setEmailNew(e.target.value)}
                        placeholder="nouvelle@adresse.fr"
                        autoComplete="email"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 sm:shrink-0">
                      <Button
                        type="button"
                        variant="secondary"
                        className="rounded-xl"
                        disabled={emailBusy}
                        onClick={() => void submitEmailChange()}
                      >
                        {emailBusy ? "Envoi…" : "Changer l’e-mail"}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="rounded-xl"
                        disabled={emailBusy}
                        onClick={() => {
                          setEditEmailOpen(false);
                          setEmailNew("");
                        }}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                  <p className="mt-3 text-[11px] leading-snug text-slate-500">
                    Selon la configuration Supabase, une confirmation peut être demandée sur la nouvelle boîte mail.
                  </p>
                </div>
              ) : null}
            </div>

            <div className="border-t border-slate-100 pt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <Label className="text-slate-800">Mot de passe</Label>
                  <p className="mt-1 text-sm text-slate-600">
                    {editPasswordOpen
                      ? "Définissez un nouveau mot de passe (vous êtes déjà connecté)."
                      : "Pour des raisons de sécurité, le mot de passe n’est pas affiché ici."}
                  </p>
                </div>
                {!editPasswordOpen ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 rounded-xl"
                    onClick={() => {
                      setEditEmailOpen(false);
                      setEmailNew("");
                      setEditPasswordOpen(true);
                    }}
                  >
                    Modifier le mot de passe
                  </Button>
                ) : null}
              </div>
              {editPasswordOpen ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="acc-pwd-new" className="text-xs text-slate-600">
                        Nouveau mot de passe
                      </Label>
                      <Input
                        id="acc-pwd-new"
                        type="password"
                        value={pwdNew}
                        onChange={(e) => setPwdNew(e.target.value)}
                        placeholder="Au moins 8 caractères"
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="acc-pwd-2" className="text-xs text-slate-600">
                        Confirmer
                      </Label>
                      <Input
                        id="acc-pwd-2"
                        type="password"
                        value={pwdConfirm}
                        onChange={(e) => setPwdConfirm(e.target.value)}
                        placeholder="Répétez le mot de passe"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-xl"
                      disabled={pwdBusy}
                      onClick={() => {
                        setEditPasswordOpen(false);
                        setPwdNew("");
                        setPwdConfirm("");
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="button"
                      className="rounded-xl"
                      disabled={pwdBusy}
                      onClick={() => void submitPasswordChange()}
                    >
                      {pwdBusy ? "Mise à jour…" : "Mettre à jour le mot de passe"}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {/* Coordonnées : par défaut, seuls les champs déjà enregistrés ; mode étendu pour tout compléter. */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-slate-900">
              <User className="h-5 w-5 text-[#2D7FF9]" />
              <h2 className="font-display text-lg font-bold">Coordonnées</h2>
            </div>
            {coordCollected.hasAny ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 rounded-xl"
                onClick={() => setCoordExpanded((v) => !v)}
              >
                {coordExpanded ? "N’afficher que les champs renseignés" : "Compléter ou modifier"}
              </Button>
            ) : null}
          </div>

          {!coordCollected.hasAny && !coordExpanded ? (
            <div className="rounded-xl bg-slate-50 px-4 py-4 text-sm text-slate-700">
              <p className="mb-3">Aucune coordonnée enregistrée pour l’instant.</p>
              <Button type="button" className="rounded-xl" onClick={() => setCoordExpanded(true)}>
                Renseigner mes coordonnées
              </Button>
            </div>
          ) : (
            <>
              <div className="mt-6 flex justify-end border-t border-slate-100 pt-4">
                <Button className="btn-primary rounded-xl px-6" onClick={() => void save()} disabled={saving}>
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start gap-2">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-[#2D7FF9]" />
            <div>
              <h2 className="font-display text-lg font-bold text-[#0B0D12]">Informations de facturation</h2>
              <p className="mt-1 text-sm text-slate-600">
                Ces données apparaissent sur vos factures en tant que client (raison sociale, adresse, SIRET, TVA).
                {billingIncomplete ? (
                  <span className="mt-1 block text-amber-800">
                    Complétez au minimum la raison sociale et l’adresse pour une facture conforme.
                  </span>
                ) : null}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="acc-bill-co">Raison sociale</Label>
              <Input
                id="acc-bill-co"
                value={form.company_name}
                onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
                placeholder="Ma Société SAS"
                autoComplete="organization"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="acc-bill-form">Forme juridique</Label>
              <Input
                id="acc-bill-form"
                value={form.company_legal_form}
                onChange={(e) => setForm((p) => ({ ...p, company_legal_form: e.target.value }))}
                placeholder="SAS, SARL, EURL…"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="acc-bill-siret">SIRET</Label>
              <Input
                id="acc-bill-siret"
                value={form.siret}
                onChange={(e) => setForm((p) => ({ ...p, siret: e.target.value }))}
                placeholder="123 456 789 00012"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="acc-bill-vat">N° TVA intracommunautaire</Label>
              <Input
                id="acc-bill-vat"
                value={form.company_vat_number}
                onChange={(e) => setForm((p) => ({ ...p, company_vat_number: e.target.value }))}
                placeholder="FR12 345 678 901"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="acc-bill-addr">Adresse</Label>
              <Input
                id="acc-bill-addr"
                value={form.company_address_line1}
                onChange={(e) => setForm((p) => ({ ...p, company_address_line1: e.target.value }))}
                placeholder="12 rue de la Paix"
                autoComplete="street-address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="acc-bill-cp">Code postal</Label>
              <Input
                id="acc-bill-cp"
                value={form.company_postal_code}
                onChange={(e) => setForm((p) => ({ ...p, company_postal_code: e.target.value }))}
                placeholder="75001"
                autoComplete="postal-code"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="acc-bill-city">Ville</Label>
              <Input
                id="acc-bill-city"
                value={form.company_city}
                onChange={(e) => setForm((p) => ({ ...p, company_city: e.target.value }))}
                placeholder="Paris"
                autoComplete="address-level2"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="acc-bill-country">Pays (code ISO)</Label>
              <Input
                id="acc-bill-country"
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.target.value.toUpperCase() }))}
                placeholder="FR"
                maxLength={2}
                autoComplete="country"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end border-t border-slate-100 pt-4">
            <Button
              className="btn-primary rounded-xl px-6"
              onClick={() => void saveBilling()}
              disabled={billingSaving}
            >
              {billingSaving ? "Enregistrement…" : "Enregistrer la facturation"}
            </Button>
          </div>
        </section>

        {/* Cartes */}
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-2">
              <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-[#2D7FF9]" />
              <div>
                <h2 className="font-display text-lg font-bold text-[#0B0D12]">Moyen de paiement</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Cartes utilisées pour l’abonnement et les achats en un clic. Les données sont chez Stripe ; vous pouvez
                  ajouter plusieurs cartes et choisir celle par défaut.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="shrink-0 rounded-xl"
              disabled={pmSetup.busy}
              onClick={() => void startAddPaymentMethod()}
            >
              {pmSetup.busy ? "Ouverture…" : "Ajouter une carte"}
            </Button>
          </div>

          <div>
            {paymentMethodsState.loading ? (
              <p className="text-sm text-slate-500">Chargement…</p>
            ) : paymentEmptyMessage ? (
              <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">{paymentEmptyMessage}</p>
            ) : (
              <ul className="space-y-2">
                {paymentMethodsState.items.map((pm) => (
                  <li
                    key={pm.id}
                    className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="text-sm text-slate-800">
                      <span className="font-semibold capitalize">{pm.brand || "Carte"}</span>
                      <span className="tabular-nums"> ····· {pm.last4 || "••••"}</span>
                      {pm.exp_month && pm.exp_year ? (
                        <span className="text-slate-600">
                          {" "}
                          · Exp. {String(pm.exp_month).padStart(2, "0")}/{String(pm.exp_year).slice(-2)}
                        </span>
                      ) : null}
                      {pm.is_default ? (
                        <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                          Par défaut
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {!pm.is_default ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="rounded-lg"
                          disabled={settingDefaultId === pm.id}
                          onClick={() => void setCardAsDefault(pm.id)}
                        >
                          {settingDefaultId === pm.id ? "…" : "Par défaut"}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-lg text-red-700 hover:bg-red-50"
                        disabled={detachingId === pm.id || paymentMethodsState.items.length === 1}
                        onClick={() => void removePaymentMethod(pm.id)}
                        title={
                          paymentMethodsState.items.length === 1
                            ? "Ajoutez une autre carte avant de supprimer la seule carte"
                            : undefined
                        }
                      >
                        {detachingId === pm.id ? "…" : "Retirer"}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <p className="mt-8 text-center text-xs text-slate-500">
          Pour les gains vendeur et la vérification d’identité, utiliser le{" "}
          <Link to="/dashboard?tab=sales" className="font-semibold text-[#2D7FF9] hover:underline">
            tableau de bord → Mes ventes
          </Link>
          .
        </p>
        </div>
      </MemberShell>

      <StripePaymentDialog
        open={pmSetup.open}
        onOpenChange={(o) =>
          setPmSetup({
            open: Boolean(o),
            clientSecret: Boolean(o) ? pmSetup.clientSecret : "",
            busy: false,
          })
        }
        clientSecret={pmSetup.clientSecret}
        intentType="setup"
        returnUrl={`${window.location.origin}/compte`}
        title="Ajouter une carte"
        subtitle={
          <span className="text-slate-600">
            La carte est enregistrée chez Stripe et peut servir pour l’abonnement et les achats.
          </span>
        }
        showEscrowInfo={false}
        confirmLabel="Enregistrer la carte"
        onSuccess={async (setupIntent) => {
          const pm =
            typeof setupIntent?.payment_method === "string"
              ? setupIntent.payment_method
              : setupIntent?.payment_method?.id;
          if (pm) {
            try {
              await api.post("/payment-methods/set-default", { payment_method_id: String(pm) });
              toast.success("Carte enregistrée (par défaut).");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Carte enregistrée ; choisissez-la par défaut dans la liste.");
            }
          }
          setPmSetup({ open: false, clientSecret: "", busy: false });
          await loadPaymentMethods();
          try {
            const me = await api.get("/subscriptions/me");
            setSubscription(me?.data?.subscription || null);
          } catch {
            setSubscription(null);
          }
        }}
      />
    </>
  );
};

export default Account;
